/**
 * @author Ketan Shetye
 * @created 2025-11-24
 * @description client admin janus component
 */

import { DataService } from './../../../../services/data.service';
import {
  Component,
  OnInit,
  HostListener,
  TemplateRef,
  OnDestroy
} from '@angular/core';
import { LoginOrientation } from '../../../../components/system/login/login.component';
import {
  WorkflowContextService,
  SecurityService,
  ClientService
} from '../../../../services';
import { Router, ActivatedRoute } from '@angular/router';
import { Actions } from '../../../../models';
import { Observable, combineLatest } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { Images, svgs } from 'src/assets/export.assets';
import { clientAdminJanusRoutesNames } from 'src/app/services/mock-data/client-admin-janus-constants/client-admin-janus-constants';

import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import { AppViewLeaveConfirmationService } from 'src/app/services/app-view-leave-confirmation.service';
import { WorkflowApplicationsJanusService } from 'src/app/views/admin/workflow-applications/workflow-applications-janus/workflow-applications-janus.service';

@Component({
  selector: 'app-client-admin-janus',
  standalone: false,
  templateUrl: './client-admin-janus.component.html',
  styleUrl: './client-admin-janus.component.scss'
})
export class ClientAdminJanusComponent implements OnInit, OnDestroy {
  clientAdminJanusAssets = {
    geoPermitsLogo: Images.geoPermitsLogo,
    dummySeal: Images.anytownSeal,
    dummyProfilePic: Images.dummyProfilePic,
    keyBoardDropDownIcon: svgs.keyboardArrowDownIcon.name,
    hamburgerIcon: svgs.hamburgerIcon.name,
    //added by furkan 17-03-2026 for mobile view primary drawer
    arrowForwardIcon: 'arrow_forward',
    arrowBackIcon: 'arrow_back',
    closeIcon: 'close'
    //ended by furkan 17-03-2026 for mobile view primary drawer
  };
  clientId: string;
  departmentId: string;
  departmentName: string;
  loginOrientation = LoginOrientation;
  canEditMapServices = false;
  canEditClients = false;
  canEditPaymentProcessorAccounts = false;
  canManageFeeRecipients = false;
  canEditExternalConnections = false;
  canEditContacts = false;
  canEditUsers = false;
  canEditRoles = false;
  canInviteUsers = false;
  canViewPaymentTransactions = false;
  canEditWorkflows = false;
  canViewDataExport = false;
  canViewCustomReports = false;
  canEditContractors = false;
  canViewInspections = false;
  canViewContractorSummary = false;
  canViewContractorFeeSummary = false;
  canViewPermitSummary = false;
  canViewDailyFees = false;
  canViewFeeRecipients = false;
  canViewPSD = false;
  canViewUserGuide = false;
  canViewRenewals = false;
  canViewMeetings = false;
  canDoAnything = false;
  canManageDataSets = false;
  canManageEmailFooters = false;
  isMobilePrimaryDrawerModalOpen = false;
  isMobileProfileMenuOpen = false;
  mobileDrawerState: 'menu' | 'departments' = 'menu';

  public innerWidth: any;

  menuItems = null;
  helpDocumentLink: any;
  userGuideSub: any;

  topMargin: number;
  departments: any[] = [];
  displayDepartments: any[] = [];
  deparmentsFetchError: string = '';
  departmentsFetching: boolean = false;
  departmentSearchQuery: string = '';
  departmentSearchFieldConfig = {
    label: 'Search',
    type: 'text',
    variant: 'outline'
  };
  handleDepartmentSearchChange(value: string): void {
    this.departmentSearchQuery = value;

    const originalList = this.departments;

    // If the search query is empty, reset the displayed list
    if (!value || value.trim() === '') {
      this.displayDepartments = [...originalList];
      return;
    }

    const searchText = value.toLowerCase().trim();

    this.displayDepartments = originalList.filter(dept =>
      dept.name.toLowerCase().includes(searchText)
    );
  }

  jurisdictionsFetchError: string = '';
  jurisdictions: any[] = [];
  displayJurisdictions: any[] = [];
  jurisdictionsFetching: boolean = false;
  jurisdictionSearchQuery: string = '';
  jurisdictionSearchFieldConfig = {
    label: 'Search',
    type: 'text',
    variant: 'outline'
  };
  handleJurisdictionSearchChange(value: string): void {
    this.jurisdictionSearchQuery = value;

    const originalList = this.jurisdictions;

    // If the search query is empty, reset the displayed list
    if (!value || value.trim() === '') {
      this.displayJurisdictions = [...originalList];
      return;
    }

    const searchText = value.toLowerCase().trim();

    this.displayJurisdictions = originalList.filter(jurisdiction =>
      jurisdiction.name.toLowerCase().includes(searchText)
    );
  }

  isMobileView = window.innerWidth <= 959;
  isMobileProfileView = window.innerWidth <= 599;
  constructor(
    public context: WorkflowContextService,
    public router: Router,
    private route: ActivatedRoute,
    private _securitySvc: SecurityService,
    private _service: DataService,
    private _clientService: ClientService,
    private appViewLeaveConfirmationService: AppViewLeaveConfirmationService,
    private workflowApplicationsJanusSvc: WorkflowApplicationsJanusService
  ) {
    // TODO: Remove this when not needed. It is here ONLY to test loading pages without any context yet.
    // this.context.client$.emit();

    this.clientId = this.route.parent.snapshot.paramMap.get('clientId');
    this.departmentId = this.route.snapshot.paramMap.get('departmentId');

    // TODO: This doesn't ALWAYS complete before child components need it to
    // if (this.clientId) {
    //   this._service.getPublicClient(this.clientId).subscribe(client => {
    //     this.context.client$.emit(client);
    //   });
    // }
  }

  activeRoute(url, exact = false) {
    return this.router.isActive(this.createUrl(url), exact);
  }

  canViewWorkflows(): boolean {
    return this.canEditWorkflows;
  }
  canViewContractors(): boolean {
    return this.canEditContractors;
  }
  canEditSecurity(): boolean {
    // Updated by Amisha on 2026-09-18.
    // Reason: keep the Janus Users menu available when Invite User is the only granted Users permission.
    return this.canEditRoles || this.canEditUsers || this.canInviteUsers;
  }

  canEditSettings(): boolean {
    return (
      this.canEditExternalConnections ||
      this.canEditMapServices ||
      this.canEditPaymentProcessorAccounts ||
      this.canManageFeeRecipients ||
      this.canManageEmailFooters ||
      this.canEditClients
    );
  }

  canViewFinancialReports(): boolean {
    return (
      this.canViewDailyFees ||
      this.canViewFeeRecipients ||
      this.canViewPaymentTransactions
    );
  }

  canViewContractorReports(): boolean {
    return this.canViewContractorSummary || this.canViewContractorFeeSummary;
  }

  canViewPermitReports(): boolean {
    return this.canViewPermitSummary;
  }

  canViewReports() {
    return (
      this.canViewDataExport ||
      this.canViewPermitReports() ||
      this.canViewFinancialReports() ||
      this.canViewContractorReports() ||
      this.canViewCustomReports
    );
  }

  canViewEmailSettings() {
    return this.canDoAnything;
  }

  canAccessMeetings() {
    return this.canViewMeetings;
  }

  canViewHelpMenu() {
    return this.canViewUserGuide;
  }

  showCommunityWorkflow() {
    return this.context.client; // this.context.client && this.location.path().indexOf('/admin/workflow') > -1;
  }

  initMenuPermissions(): Observable<any> {
    const result$ = combineLatest(
      this._securitySvc.isLoginEntitled(Actions.MAP_SERVICE_MANAGE).pipe(
        map(r => {
          this.canEditMapServices = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.CLIENTS_MANAGE_SETTINGS).pipe(
        map(r => {
          this.canEditClients = r;
          return r;
        })
      ),
      this._securitySvc
        .isLoginEntitled(Actions.PAYMENT_PROCESSOR_ACCOUNT_MANAGE)
        .pipe(
          map(r => {
            this.canEditPaymentProcessorAccounts = r;
            return r;
          })
        ),
      this._securitySvc.isLoginEntitled(Actions.MANAGE_FEE_RECIPIENTS).pipe(
        map(r => {
          this.canManageFeeRecipients = r;
          return r;
        })
      ),
      this._securitySvc
        .isLoginEntitled(Actions.EXTERNAL_CONNECTION_MANAGE)
        .pipe(
          map(r => {
            this.canEditExternalConnections = r;
            return r;
          })
        ),
      this._securitySvc.isLoginEntitled(Actions.USER_MANAGE).pipe(
        map(r => {
          this.canEditUsers = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.ROLE_MANAGE).pipe(
        map(r => {
          this.canEditRoles = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.REPORT_DATA_EXPORT_VIEW).pipe(
        map(r => {
          this.canViewDataExport = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.VIEW_CUSTOM_REPORTS).pipe(
        map(r => {
          this.canViewCustomReports = r;
          return r;
        })
      ),
      this._securitySvc
        .isLoginEntitled(Actions.REPORT_PAYMENT_TRANSACTION_VIEW)
        .pipe(
          map(r => {
            this.canViewPaymentTransactions = r;
            return r;
          })
        ),
      this._securitySvc
        .isLoginEntitled(null, null, Actions.WORKFLOW_ACTIONS)
        .pipe(
          map(r => {
            this.canEditWorkflows = r;
            return r;
          })
        ),
      this._securitySvc
        .isLoginEntitled(null, null, Actions.CONTRACTOR_ACTIONS)
        .pipe(
          map(r => {
            this.canEditContractors = r;
            return r;
          })
        ),
      this._securitySvc.isLoginEntitled(Actions.VIEW_INSPECTIONS).pipe(
        map(r => {
          this.canViewInspections = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.VIEW_CONTRACTOR_SUMMARY).pipe(
        map(r => {
          this.canViewContractorSummary = r;
          return r;
        })
      ),
      this._securitySvc
        .isLoginEntitled(Actions.VIEW_CONTRACTOR_FEE_SUMMARY)
        .pipe(
          map(r => {
            this.canViewContractorFeeSummary = r;
            return r;
          })
        ),
      this._securitySvc.isLoginEntitled(Actions.VIEW_PERMIT_SUMMARY).pipe(
        map(r => {
          this.canViewPermitSummary = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.VIEW_PREVIOUS_SYSTEM_DATA).pipe(
        map(r => {
          this.canViewPSD = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.RENEWALS_VIEW).pipe(
        map(r => {
          this.canViewRenewals = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.VIEW_USER_GUIDE).pipe(
        map(r => {
          this.canViewUserGuide = r;
          return r;
        })
      ),
      this._securitySvc
        .isLoginEntitled(null, null, Actions.AGENDA_ACTIONS)
        .pipe(
          map(r => {
            this.canViewMeetings = r;
            return r;
          })
        ),
      this._securitySvc.isLoginEntitled(Actions.INVITE_USER).pipe(
        map(r => {
          this.canInviteUsers = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.DO_ANYTHING).pipe(
        map(r => {
          this.canDoAnything = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.MANAGE_DATASETS).pipe(
        map(r => {
          this.canManageDataSets = r;
          return r;
        })
      ),
      this._securitySvc.isLoginEntitled(Actions.MANAGE_EMAIL_FOOTERS).pipe(
        map(r => {
          this.canManageEmailFooters = r;
          return r;
        })
      )
    );

    return result$;
  }

  getDefaultLink(menuItem): string {
    if (menuItem.children) {
      const viewableChildren = menuItem.children.filter(
        c => c.canView === true
      );

      if (viewableChildren && viewableChildren.length > 0) {
        return this.createUrl(viewableChildren[0].url);
      }
    } else {
      return this.createUrl(menuItem.url);
    }
    return null;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.innerWidth = window.innerWidth;
    this.isMobileView = window.innerWidth <= 959; 
    this.isMobileProfileView = window.innerWidth <= 599;
    //added by furkan 17-03-2026 for mobile view primary drawer
    if (this.innerWidth >= 960 && this.isMobilePrimaryDrawerModalOpen) {
      this.isMobilePrimaryDrawerModalOpen = false;
    }
    if (!this.isMobileProfileView) {
      this.isMobileProfileMenuOpen = false;
    }
    //ended by furkan 17-03-2026
  }

  get sidebarClass() {
    return this.innerWidth < 768 ? 'bmd-drawer-overlay' : '';
  }

  createUrl(url: string) {
    const hasValidUrlProtocol = (urltest = '') =>
      ['http://', 'https://', 'ftp://'].some(protocol => {
        return urltest.startsWith(protocol);
      });
    if (hasValidUrlProtocol(url)) {
      return url;
    } else {
      return `/admin/jurisdiction/${this.clientId}/department/${this.departmentId}${url}`;
    }
  }

  // Start code added by Sanchit on 03/16/2026
  isDefaultLogo = false;

  setDefaultLogo(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/images/geopermits-logo/geopermits-logo.png';
    img.parentElement?.classList.add('default-bg');
  }
  // End code added by Sanchit on 03/16/2026
  ngOnInit() {
    this.context.janusLayout$.emit('staff');
    this.innerWidth = window.innerWidth;
    this.helpDocumentLink = '';
    this.getJuirisdictions();
    this.userGuideSub = this._service.getUserGuideLink().subscribe(results => {
      this.helpDocumentLink = results;
    });
    this.route.paramMap.subscribe(params => {
      this.departmentId = params.get('departmentId');
      this.context.staffDepartmentId$.emit(this.departmentId);
    });
    this.route.parent.paramMap.subscribe(params => {
      this.clientId = params.get('clientId');
      this._service.getPublicClient(this.clientId).subscribe(client => {
        this.context.client$.emit(client);
      });
      this.getDepartmentsBasedOnJurisdiction(this.clientId);
      this.initMenuPermissions().subscribe(a => {
        this.menuItems = [
          {
            name: clientAdminJanusRoutesNames.workflowApplications.name,
            url: clientAdminJanusRoutesNames.workflowApplications.routeName,
            canView: true
          },
          {
            name: clientAdminJanusRoutesNames.renewals.name,
            url: clientAdminJanusRoutesNames.renewals.routeName,
            canView: this.canViewRenewals
          },
          {
            name: clientAdminJanusRoutesNames.contractors.name,
            url: clientAdminJanusRoutesNames.contractors.routeName,
            canView: this.canViewContractors()
          },
          {
            name: clientAdminJanusRoutesNames.inspections.name,
            url: clientAdminJanusRoutesNames.inspections.routeName,
            canView: this.canViewInspections
          },
          {
            name: clientAdminJanusRoutesNames.reports.name,
            url: clientAdminJanusRoutesNames.reports.routeName,
            canView: this.canViewReports()
          },
          {
            name: clientAdminJanusRoutesNames.meetings.name,
            url: clientAdminJanusRoutesNames.meetings.routeName,
            // canView: this.canAccessMeetings(),
            canView: false
          },
          {
            name: clientAdminJanusRoutesNames.users.name,
            url: clientAdminJanusRoutesNames.users.routeName,
            canView: this.canEditSecurity()
          },
          {
            name: clientAdminJanusRoutesNames.settings.name,
            url: clientAdminJanusRoutesNames.settings.routeName,
            canView: this.canEditSettings()
          },
          {
            name: clientAdminJanusRoutesNames.workflows.name,
            url: clientAdminJanusRoutesNames.workflows.routeName,
            canView: this.canViewWorkflows()
          },
          {
            name: clientAdminJanusRoutesNames.help.name,
            url: clientAdminJanusRoutesNames.help.routeName,
            canView: this.canViewHelpMenu()
          }
        ];
      });
    });
  }

  ngOnDestroy(): void {
    this.context.staffDepartmentId$.emit(null);
    this.setStaffDepartmentName();
  }

  //added by furkan 17-03-2026 for mobile view primary drawer
  getCurrentMenuName(): string {
    if (!this.menuItems) return 'Applications';
    const activeItem = this.menuItems.find((item: any) =>
      this.isActive(item.url)
    );
    return activeItem ? activeItem.name : 'Applications';
  }
  //ended by furkan 17-03-2026

  // Added by Amisha on 2026-09-03.
  // Reason: Client Admin Janus already resolves the active department, so routed children can reuse its name without fetching departments again.
  private setStaffDepartmentName(departmentName?: string): void {
    this.context.staffDepartmentName = departmentName?.trim() || undefined;
  }

  getDepartmentsBasedOnJurisdiction = (
    clientId: string,
    isJurisdictionSwitched?: boolean
  ) => {
    this.deparmentsFetchError = '';
    this.departmentsFetching = true;
    this.setStaffDepartmentName();
    this._service
      .getClientDepartments(clientId)
      .pipe(
        finalize(() => {
          this.departmentsFetching = false;
        })
      )
      .subscribe({
        next: response => {
          this.departments = response;
          this.displayDepartments = response;
          if (isJurisdictionSwitched === true) {
            this.departmentName = this.departments[0].name;
            this.departmentId = this.departments[0].id;
            this.context.staffDepartmentId$.emit(this.departments[0].id);

            this.router.navigate([
              '/admin/jurisdiction',
              clientId,
              'department',
              this.departmentId
            ]);
          } else {
            this.departmentName = this.departments.find(
              dept => dept.id === this.departmentId
            )?.name;
          }
          this.setStaffDepartmentName(this.departmentName);
        },
        error: _err => {
          this.deparmentsFetchError = 'some error occured';
          this.setStaffDepartmentName();
        }
      });
  };
  getJuirisdictions = () => {
    this.jurisdictionsFetchError = '';
    this.jurisdictionsFetching = true;
    this._clientService
      .getAdminClients()
      .pipe(
        finalize(() => {
          this.jurisdictionsFetching = false;
        })
      )
      .subscribe({
        next: response => {
          this.jurisdictions = response;
          this.displayJurisdictions = response;
        },
        error: _err => {
          this.jurisdictionsFetchError =
            'error occured while fetching jurisdictions';
        }
      });
  };

  setTopMarginForNavbar(pixelHeight: number) {
    this.topMargin = pixelHeight;
  }
  getPrimaryDrawerButtonData(
    template: TemplateRef<any>,
    url: string,
    isMobile = false
  ) {
    return {
      text: template,
      onClick: async () => {
        // Added by Amisha on 2026-06-19.
        // Reason: clicking the already-active Janus sidebar section was
        // collapsing an open AppView without showing the shared leave prompt.
        if (this.isActive(url) && this.isAppViewCurrentlyOpen()) {
          const shouldLeave =
            await this.appViewLeaveConfirmationService.requestConfirmation();

          if (!shouldLeave) {
            return;
          }
        }

        this.router.navigateByUrl(this.createUrl(`/${url}`));
        if (isMobile) {
          this.closeMobileDrawer();
        }
      }
    };
  }
  isActive(url: string): boolean {
    return this.router.url.includes(`/${url}`);
  }

  // Added by Amisha on 2026-06-19.
  // Reason: reuse the same Janus AppView detection for active sidebar clicks
  // so same-section navigation also respects the shared leave confirmation.
  private isAppViewCurrentlyOpen(): boolean {
    return (
      this.router.url.includes('/application/') ||
      this.router.parseUrl(this.router.url).queryParams['showAppView'] === '1'
    );
  }

  // Added by Amisha on 2026-06-26.
  // Reason: top Janus shell actions like department/jurisdiction/profile/logo
  // navigation bypass child-route guards, so they need the shared AppView
  // confirmation check before leaving an edited AppView.
  private async canLeaveCurrentAppView(): Promise<boolean> {
    if (!this.isAppViewCurrentlyOpen()) {
      return true;
    }

    return this.appViewLeaveConfirmationService.requestConfirmation();
  }

  getHamburgerIconButtonData(template: TemplateRef<any>) {
    return {
      text: template,
      onClick: () => {
        this.openMobileDrawer();
      }
    };
  }

  //added by furkan 17-03-2026 for mobile view primary drawer
  openMobileDrawer() {
    this.isMobilePrimaryDrawerModalOpen = true;
    this.mobileDrawerState = 'menu';
  }

  closeMobileDrawer() {
    this.isMobilePrimaryDrawerModalOpen = false;
    this.mobileDrawerState = 'menu';
  }

  openMobileDepartments() {
    this.mobileDrawerState = 'departments';
  }

  closeMobileDepartments() {
    this.mobileDrawerState = 'menu';
  }

  openMobileProfileMenu(event: MouseEvent): void {
    if (!this.isMobileProfileView) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.isMobileProfileMenuOpen = true;
  }

  closeMobileProfileMenu(): void {
    this.isMobileProfileMenuOpen = false;
  }

  handleMobileEditProfile(): void {
    this.closeMobileProfileMenu();
    void this.getEditProfileButtonData().onClick();
  }

  handleMobileSignOut(): void {
    this.closeMobileProfileMenu();
    this.onSignOutClick().onClick();
  }
  //ended by furkan 17-03-2026

  private removeWorkflowNameFilter(): void {
  const cache = localStorage.getItem('workflow-applications-filters');

  if (cache) {
    const parsed = JSON.parse(cache);

    parsed.attributeFilterInputs =
      (parsed.attributeFilterInputs || []).filter(
        (f: any) => f.title !== 'WORKFLOW_NAME'
      );

    localStorage.setItem(
      'workflow-applications-filters',
      JSON.stringify(parsed)
    );
  }

  const current = this.workflowApplicationsJanusSvc.getData();

  const updatedFilters = (current.attributeFilterInputs || []).filter(
    (item: any) => item.title !== 'WORKFLOW_NAME'
  );

  this.workflowApplicationsJanusSvc.setData({
    ...current,
    attributeFilterInputs: updatedFilters
  });
}

  onDepartmentMenuItemClick = (dept: any, isMobile = false) => {
    return {
      text: dept.name,
      onClick: async () => {
        // Added by Amisha on 2026-06-26.
        // Reason: department switching is a top-level Janus shell action, so
        // it must respect the shared AppView leave confirmation before exit.
        const shouldLeave = await this.canLeaveCurrentAppView();

        if (!shouldLeave) {
          return;
        }

        this.removeWorkflowNameFilter();

        this.context.staffDepartmentId$.emit(dept.id);
        this.departmentId = dept.id;
        this.departmentName = dept.name;
        this.setStaffDepartmentName(dept.name);
        const currentUrl = this.router.url;
        const updatedUrl = currentUrl.replace(
          /\/department\/[^\/]+/,
          `/department/${this.departmentId}`
        );

        this.router.navigateByUrl(updatedUrl.split('?')[0]); // removes query params
        if (isMobile) {
          this.closeMobileDrawer();
        }
      }
    };
  };

  onJurisdictionMenuItemClick = (jurisdiction: any) => {
    return {
      text: jurisdiction.name,

      onClick: async () => {
        // Added by Amisha on 2026-06-26.
        // Reason: jurisdiction switching bypasses child-route guards, so check
        // the shared Janus AppView leave prompt before navigating away.
        const shouldLeave = await this.canLeaveCurrentAppView();

        if (!shouldLeave) {
          return;
        }

        this.removeWorkflowNameFilter();

        const jurisdictionId = jurisdiction.id;
        const useNewLayout = jurisdiction?.useNewLayout
          ? jurisdiction.useNewLayout
          : false;
        this.context.useNewLayout$.emit(useNewLayout);
        if (useNewLayout) {
          this.getDepartmentsBasedOnJurisdiction(jurisdictionId, true);
        } else {
          this.context.staffDepartmentId$.emit(null);
          this.setStaffDepartmentName();
          // geopermit redirections
          this.router.navigate([
            '/admin/jurisdiction',
            jurisdictionId,
            'workflows'
          ]);
        }
      }
    };
  };
  getEditProfileButtonData = () => {
    return {
      text: 'Edit Profile',
      onClick: async () => {
        // Added by Amisha on 2026-06-26.
        // Reason: profile navigation leaves the Janus shell directly, so it
        // needs the same shared AppView confirmation as sidebar exits.
        const shouldLeave = await this.canLeaveCurrentAppView();

        if (!shouldLeave) {
          return;
        }

        this.router.navigate(['/auth', 'edit-profile'], {
          queryParams: { from: 'staff' }
        });
      }
    };
  };

  signOutModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Confirm Sign Out',
    modalSubDescp:
      'This will end your session, are you sure you want to sign out?',
    yesNoBtn: true,
    okBtn: false,
    closeBtn: false,
    inTime: 300,
    outTime: 200,
    status: 'default'
  };
  onSignOutClick = () => {
    return {
      text: 'Sign Out',
      onClick: () => {
        this.signOutModalData = {
          ...this.signOutModalData,
          open: true
        };
      }
    };
  };
  handleSignOutModalAccepted() {
    this._securitySvc.logout().subscribe(() => {
      this.router.navigate(['/']);
    });
  }

  handleSignOutModalModalRejected() {}

  onGeoPermitLogoClick = () => {
    // Added by Amisha on 2026-06-26.
    // Reason: logo/home navigation bypasses the child-route guard path, so it
    // must manually reuse the shared Janus AppView leave confirmation.
    this.canLeaveCurrentAppView().then(shouldLeave => {
      if (!shouldLeave) {
        return;
      }

      this.router.navigate(['/']);
    });
  };
}
